import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { RouterLink } from '@angular/router';
import { BusinessService } from '../../services/business.service';

/**
 * SCRUM-262/264/266 — shared seller pieces for the homepage carousels
 * (car / nadlan / sale2 / pet bars), matching the shop list card:
 *   <app-card-biz-badges>  advertiser-type pill + "ממומן" on the photo (bottom-left)
 *   <app-card-dealer>      avatar + name + role, links to the seller page
 * Internal (site) ads get neither.
 */
function isInternal(item: any): boolean {
  return String(item?.resourcetype || '').toLowerCase() === 'internalads';
}

@Component({
  selector: 'app-card-biz-badges',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (!internal && (tag || sponsored)) {
      <div class="biz-badges" [style.left.px]="left" [style.bottom.px]="bottom">
        @if (sponsored) {
          <span class="biz-badge biz-badge--sponsored"><i class="mdi mdi-bullhorn"></i> ממומן</span>
        }
        @if (tag) {
          <span class="biz-badge biz-badge--biz">
            <i class="mdi {{ icon }}"></i> {{ tag }}
            @if (verified) { <i class="mdi mdi-check-circle biz-badge-verified" title="עסק מאומת"></i> }
          </span>
        }
      </div>
    }
  `,
  styles: [`
    .biz-badges {
      position: absolute; z-index: 4;
      display: flex; flex-direction: column; align-items: flex-start; gap: 4px;
      pointer-events: none;
    }
    .biz-badge {
      display: inline-flex; align-items: center; gap: 5px;
      padding: 4px 10px; border-radius: 999px;
      font-size: 11px; font-weight: 800; line-height: 1.2; white-space: nowrap;
      box-shadow: 0 2px 8px rgba(15, 23, 42, .18);
    }
    .biz-badge i { font-size: 13px; line-height: 1; }
    .biz-badge--biz { background: #fff; color: #1d4ed8; border: 2px solid #2563eb; }
    .biz-badge--sponsored { background: #0f172a; color: #fff; border: 2px solid #fff; }
    .biz-badge-verified { color: #16a34a; }
  `],
})
export class CardBizBadgesComponent {
  @Input() item: any;
  /** px from the photo's left edge (pet cards shift it past the gender dot) */
  @Input() left = 8;
  @Input() bottom = 8;

  get internal(): boolean { return isInternal(this.item); }
  get tag(): string | null { return BusinessService.tagFor(this.item); }
  get sponsored(): boolean { return this.item?.sponsored === true; }
  get verified(): boolean { return !!this.item?.User?.store?.verified; }
  get icon(): string {
    switch (String(this.item?.resourcetype || '').toLowerCase()) {
      case 'car':    return 'mdi-car';
      case 'nadlan': return 'mdi-home';
      default:       return 'mdi-store';
    }
  }
}

@Component({
  selector: 'app-card-dealer',
  standalone: true,
  imports: [RouterLink],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (sellerId) {
      <a class="dealer" [class.dealer--private]="!tag"
         [routerLink]="['/seller/' + sellerId]"
         (click)="$event.stopPropagation()"
         (keydown.enter)="$event.stopPropagation()"
         [attr.aria-label]="'לעמוד המוכר: ' + name">
        <span class="dealer-avatar">
          @if (avatar) {
            <img [src]="avatar" [alt]="name" loading="lazy" (error)="avatarFailed = true">
          } @else if (tag) {
            <span class="dealer-initials">{{ initials }}</span>
          } @else {
            <i class="mdi mdi-account dealer-person"></i>
          }
        </span>
        <span class="dealer-text">
          <span class="dealer-name">{{ name }}
            @if (verified) { <i class="mdi mdi-check-circle dealer-verified" title="עסק מאומת"></i> }
          </span>
          <span class="dealer-role">{{ tag || 'מוכר פרטי' }}</span>
        </span>
      </a>
    } @else if (fallbackName) {
      <span class="dealer dealer--private dealer--static">
        <span class="dealer-avatar"><i class="mdi mdi-account dealer-person"></i></span>
        <span class="dealer-text"><span class="dealer-name">{{ fallbackName }}</span></span>
      </span>
    }
  `,
  styles: [`
    :host { display: block; flex: 1 1 auto; min-width: 0; }
    .dealer {
      display: flex; align-items: center; gap: 8px; min-width: 0;
      text-decoration: none; color: inherit; border-radius: 8px;
    }
    .dealer:hover .dealer-name { color: #2563eb; }
    .dealer--static { cursor: default; }
    .dealer--static:hover .dealer-name { color: #0f172a; }
    .dealer-avatar {
      flex-shrink: 0; width: 32px; height: 32px; border-radius: 9px; overflow: hidden;
      background: #eff6ff; border: 1px solid #e2e8f0;
      display: flex; align-items: center; justify-content: center;
    }
    .dealer-avatar img { width: 100%; height: 100%; object-fit: cover; }
    .dealer-initials { font-size: 12px; font-weight: 800; color: #2563eb; }
    .dealer-person { font-size: 18px; color: #94a3b8; line-height: 1; }
    .dealer--private .dealer-avatar { background: #f1f5f9; border-radius: 50%; }
    .dealer-text { display: flex; flex-direction: column; min-width: 0; flex: 1; line-height: 1.25; text-align: start; }
    .dealer-name {
      font-size: 12px; font-weight: 700; color: #0f172a;
      overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
      transition: color .2s ease-out;
    }
    .dealer-verified { color: #16a34a; font-size: 13px; margin-inline-start: 2px; }
    .dealer-role { font-size: 10.5px; color: #64748b; font-weight: 600; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  `],
})
export class CardDealerComponent {
  @Input() item: any;
  avatarFailed = false;

  get sellerId(): number | null {
    return isInternal(this.item) ? null : (this.item?.User?.id ?? null);
  }
  get tag(): string | null { return BusinessService.tagFor(this.item); }
  get verified(): boolean { return !!this.item?.User?.store?.verified; }
  get name(): string {
    const u = this.item?.User || {};
    return u.store?.name
      || [u.first_name, u.last_name].filter(Boolean).join(' ').trim()
      || this.item?.userContactDetails?.contactPerson
      || (this.tag ? 'עסק' : 'מוכר פרטי');
  }
  /** Internal ads / rows without a User id keep the plain contact name. */
  get fallbackName(): string {
    return this.item?.userContactDetails?.contactPerson || '';
  }
  get avatar(): string | null {
    if (this.avatarFailed) return null;
    const u = this.item?.User;
    return u?.store?.logo || u?.avatar || u?.profilePhoto || null;
  }
  get initials(): string {
    const parts = this.name.trim().split(/\s+/).filter(Boolean);
    return ((parts[0]?.charAt(0) || '') + (parts[1]?.charAt(0) || '')).toUpperCase() || '?';
  }
}
